import { RefObject, useLayoutEffect, useRef, useState } from "react";

/**
 * Should the footer's Notes number yield to the timer? The job-hours pill is
 * never hidden (its slot reserves the short form's width - see
 * JobHourDisplay), so when the footer is too narrow the TIMER is what gets
 * clipped. The Notes number yields before that happens: true only when
 * (a) with the number shown the timer is clipped, AND (b) hiding the number
 * (numW = its width + the 1px icon gap) would stop the clipping.
 *
 * Everything is expressed as `deficit`: how many px short of its natural width
 * the timer is with the number SHOWN (positive: clipped; negative: spare room,
 * which the hours slot absorbs above its floor).
 *   clipped   = timer.scrollWidth - timer.clientWidth
 *   slotExtra = max(0, slot.clientWidth - short.offsetWidth)
 *   deficit   = clipped - slotExtra + (compact now ? numW : 0)
 * (while compact the number's room is already added, so add numW back to
 * recover the shown-number figure; that is also what stops oscillation: the
 * decision is a function of `deficit`, not of the current rendering.)
 *
 * Enter rule: `deficit > 0 && deficit - numW <= 0`. Stay rule (while already
 * compact): `deficit > -1 && deficit - numW <= 1`, i.e. 1px of hysteresis on
 * each side, because the widths are integer-rounded, so the estimate can be
 * off by 1px and would otherwise flip on every resize frame at a boundary.
 *
 * `timer.scrollWidth` is only an honest natural width because the compact
 * timer's Button is `justify-start` (see CardTimer): overflow is then one-sided
 * (to the right), so scrollWidth - clientWidth is the real clipped amount (less
 * a constant 3px of right padding, identical in the enter and stay states, so
 * the fixed point holds). Verified in a real browser: with the Button centered
 * it under-reports by about half and the decision flickers.
 *
 * Reads four elements under `rootRef` (see JobHourDisplay / CardTimer /
 * ActivityButton): [data-hours-slot], [data-hours-measure="short"],
 * [data-footer-timer], [data-notes-count-measure]; without all four it never
 * compacts. `deps` re-run the fit; a ResizeObserver on those four elements AND
 * the timer's descendants covers resizes (the timer's own box does not change
 * while it is clipped, but its text's box does).
 */
export const useCompactNotes = (
  rootRef: RefObject<HTMLElement>,
  deps: ReadonlyArray<unknown> = []
): boolean => {
  const [compact, setCompact] = useState(false);
  // What the DOM currently reflects (the last committed render).
  const renderedRef = useRef(false);
  useLayoutEffect(() => {
    renderedRef.current = compact;
  });

  useLayoutEffect(() => {
    const root = rootRef.current;
    if (!root) return;
    const slot = root.querySelector<HTMLElement>("[data-hours-slot]");
    const short = root.querySelector<HTMLElement>(
      '[data-hours-measure="short"]'
    );
    const timer = root.querySelector<HTMLElement>("[data-footer-timer]");
    const count = root.querySelector<HTMLElement>("[data-notes-count-measure]");
    if (!slot || !short || !timer || !count) {
      setCompact(false);
      return;
    }
    const fit = () => {
      const numW = count.offsetWidth + 1;
      const slotExtra = Math.max(0, slot.clientWidth - short.offsetWidth);
      const clipped = timer.scrollWidth - timer.clientWidth;
      const rendered = renderedRef.current;
      const deficit = clipped - slotExtra + (rendered ? numW : 0);
      setCompact(
        rendered
          ? deficit > -1 && deficit - numW <= 1
          : deficit > 0 && deficit - numW <= 0
      );
    };
    fit();
    if (typeof ResizeObserver === "undefined") return;
    const observer = new ResizeObserver(fit);
    observer.observe(slot);
    observer.observe(short);
    observer.observe(count);
    observer.observe(timer);
    timer.querySelectorAll("*").forEach((el) => observer.observe(el));
    return () => observer.disconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rootRef, ...deps]);

  return compact;
};
